import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Alert,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useAuth } from '../../../../contexts/AuthContext';
import SignaturePad from '../SignaturePad';
import ManagerSelect from '../ManagerSelect';
import { RichText, getFormText, NO_AUTOFILL } from '../PolicyText';

const CellPhoneForm = ({ language = 'en', onSubmit, loading, disabled }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    employeeName: '',
    managerName: '',
    serialNumber: ''
  });

  useEffect(() => {
    if (user) {
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username;
      setFormData(prev => ({
        ...prev,
        employeeName: fullName
      }));
    }
  }, [user]);
  const [employeeSignature, setEmployeeSignature] = useState(null);
  const [managerSignature, setManagerSignature] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [errors, setErrors] = useState({});
  const [missingFieldLabels, setMissingFieldLabels] = useState([]);

  const t = {
    en: {
      title: 'iPad - Cell Phone Policy Acknowledgment',
      equipment: 'Equipment Details',
      policyTitle: 'Policy Agreement',
      employeeName: 'Employee Name',
      employeeSignature: 'Employee Signature',
      managerName: 'Manager Name',
      managerSignature: 'Manager Signature',
      serialNumber: 'Device Serial Number',
      save: 'Save Form',
      requiredField: 'This field is required',
      signatureRequired: 'Signature is required',
      acknowledge: 'I acknowledge receipt of the equipment above and agree to the policy terms.',
      acknowledgeRequired: 'You must acknowledge receipt of the equipment',
      managerNote: 'To be completed by your manager when the equipment is handed over.',
      fixErrors: 'Please complete the following before saving: '
    },
    es: {
      title: 'Reconocimiento de Política de iPad - Teléfono Celular',
      equipment: 'Detalles del Equipo',
      policyTitle: 'Acuerdo de Política',
      employeeName: 'Nombre del Empleado',
      employeeSignature: 'Firma del Empleado',
      managerName: 'Nombre del Gerente',
      managerSignature: 'Firma del Gerente',
      serialNumber: 'Número de Serie del Dispositivo',
      save: 'Guardar Formulario',
      requiredField: 'Este campo es obligatorio',
      signatureRequired: 'Se requiere firma',
      acknowledge: 'Reconozco haber recibido el equipo indicado arriba y acepto los términos de la política.',
      acknowledgeRequired: 'Debe reconocer la recepción del equipo',
      managerNote: 'Debe ser completado por su gerente al entregar el equipo.',
      fixErrors: 'Complete lo siguiente antes de guardar: '
    }
  };

  const labels = t[language] || t.en;
  // Legacy PL Hub agreement wording (translation.json employeeForms.text.cellPhone).
  const agreement = getFormText(language, 'text.cellPhone.paragraphs');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
      setMissingFieldLabels(prev => prev.filter(l => l !== labels[name]));
    }
  };

  // TKT-276: a failed save used to show only small captions beside the
  // individual fields, which on this long form sit well below the fold - so
  // pressing Save looked like it did nothing at all. Collect what's missing
  // and list it at the top, the same way the Uniform form does (TKT-273).
  const validate = () => {
    const newErrors = {};
    const missing = [];

    if (!formData.employeeName.trim()) {
      newErrors.employeeName = labels.requiredField;
      missing.push(labels.employeeName);
    }
    if (!formData.serialNumber.trim()) {
      newErrors.serialNumber = labels.requiredField;
      missing.push(labels.serialNumber);
    }
    if (!acknowledged) {
      newErrors.acknowledged = labels.acknowledgeRequired;
      missing.push(labels.acknowledge);
    }
    if (!employeeSignature) {
      newErrors.employeeSignature = labels.signatureRequired;
      missing.push(labels.employeeSignature);
    }
    if (!formData.managerName.trim()) {
      newErrors.managerName = labels.requiredField;
      missing.push(labels.managerName);
    }
    if (!managerSignature) {
      newErrors.managerSignature = labels.signatureRequired;
      missing.push(labels.managerSignature);
    }

    setErrors(newErrors);
    setMissingFieldLabels(missing);
    return missing.length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validate()) {
      // Combine both signatures into one field
      const combinedData = {
        ...formData,
        acknowledged: true,
        employeeSignature,
        managerSignature
      };
      onSubmit(combinedData, employeeSignature); // Primary signature for display
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} autoComplete="off" data-lpignore="true" data-form-type="other">
      {missingFieldLabels.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }} role="alert">
          {labels.fixErrors}{missingFieldLabels.join(', ')}
        </Alert>
      )}

      {/* Equipment Details */}
      <Typography variant="h6" gutterBottom>
        {labels.equipment}
      </Typography>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label={labels.serialNumber}
            name="serialNumber"
            value={formData.serialNumber}
            onChange={handleChange}
            required
            disabled={disabled}
            error={!!errors.serialNumber}
            helperText={errors.serialNumber}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Policy Agreement */}
      <Typography variant="h6" gutterBottom>
        {labels.policyTitle}
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        {(Array.isArray(agreement) ? agreement : []).map((paragraph, index) => (
          <Typography key={index} variant="body2" paragraph>
            <RichText text={paragraph} values={{ name: formData.employeeName, serial: formData.serialNumber }} />
          </Typography>
        ))}
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Employee Section */}
      <Typography variant="h6" gutterBottom>
        {labels.employeeName}
      </Typography>

      <Grid container spacing={2} sx={{ mt: 1, mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label={labels.employeeName}
            name="employeeName"
            value={formData.employeeName}
            onChange={handleChange}
            required
            disabled={disabled}
            error={!!errors.employeeName}
            helperText={errors.employeeName}
            inputProps={NO_AUTOFILL}
          />
        </Grid>
      </Grid>

      {/* TKT-276: the form displayed the acknowledgement wording but gave the
          employee nothing to actually acknowledge with. */}
      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={acknowledged}
              onChange={(e) => {
                setAcknowledged(e.target.checked);
                if (e.target.checked) {
                  setErrors((prev) => ({ ...prev, acknowledged: '' }));
                  setMissingFieldLabels((prev) => prev.filter((l) => l !== labels.acknowledge));
                }
              }}
              disabled={disabled}
              inputProps={{ 'aria-label': labels.acknowledge }}
            />
          }
          label={labels.acknowledge}
        />
        {errors.acknowledged && (
          <Typography color="error" variant="caption" display="block">
            {errors.acknowledged}
          </Typography>
        )}
      </Box>

      <SignaturePad
        onSignature={(sig) => {
          setEmployeeSignature(sig);
          if (sig) {
            setErrors((prev) => ({ ...prev, employeeSignature: '' }));
            setMissingFieldLabels((prev) => prev.filter((l) => l !== labels.employeeSignature));
          }
        }}
        label={labels.employeeSignature}
        language={language}
      />
      {errors.employeeSignature && (
        <Typography color="error" variant="caption" display="block" sx={{ mt: 1 }}>
          {errors.employeeSignature}
        </Typography>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Manager Section */}
      <Typography variant="h6" gutterBottom>
        {labels.managerName}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {labels.managerNote}
      </Typography>

      <Grid container spacing={2} sx={{ mt: 1, mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <ManagerSelect
            label={labels.managerName}
            value={formData.managerName}
            onChange={(managerName) => {
              setFormData((prev) => ({ ...prev, managerName }));
              if (errors.managerName) setErrors((prev) => ({ ...prev, managerName: '' }));
              if (managerName) setMissingFieldLabels((prev) => prev.filter((l) => l !== labels.managerName));
            }}
            required
            disabled={disabled}
            error={!!errors.managerName}
            helperText={errors.managerName}
            language={language}
          />
        </Grid>
      </Grid>

      <SignaturePad
        onSignature={(sig) => {
          setManagerSignature(sig);
          if (sig) {
            setErrors((prev) => ({ ...prev, managerSignature: '' }));
            setMissingFieldLabels((prev) => prev.filter((l) => l !== labels.managerSignature));
          }
        }}
        label={labels.managerSignature}
        language={language}
      />
      {errors.managerSignature && (
        <Typography color="error" variant="caption" display="block" sx={{ mt: 1 }}>
          {errors.managerSignature}
        </Typography>
      )}

      {/* Submit Button */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="submit"
          variant="contained"
          size="large"
          startIcon={<SaveIcon />}
          disabled={disabled || loading}
        >
          {labels.save}
        </Button>
      </Box>
    </Box>
  );
};

export default CellPhoneForm;

